import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import {
  markAttendance,
  clockOut,
  getAttendanceStatus,
  applyLeave,
} from "../../../services/attendance";

const ATTENDANCE_TYPES = [
  { value: "present", label: "Present", icon: "fa-check-circle", color: "#28a745" },
  { value: "absent", label: "Absent", icon: "fa-times-circle", color: "#dc3545" },
  { value: "leave", label: "Leave", icon: "fa-calendar-alt", color: "#f9a825" },
  { value: "on_duty", label: "On Duty", icon: "fa-briefcase", color: "#17a2b8" },
  { value: "lop", label: "LOP", icon: "fa-exclamation-triangle", color: "#e67e22" },
];

export default function ClockInOut({ embedded = false } = {}) {
  const [attendance, setAttendance] = useState(null);
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [cutoff, setCutoff] = useState("");
  const [requiresClocking, setRequiresClocking] = useState(true);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showLeave, setShowLeave] = useState(false);
  const [leaveFrom, setLeaveFrom] = useState("");
  const [leaveTo, setLeaveTo] = useState("");
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveType, setLeaveType] = useState("leave");
  const [pendingLeave, setPendingLeave] = useState(null);
  const [activeLeave, setActiveLeave] = useState(null);

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    setLoading(true);
    const res = await getAttendanceStatus();
    if (res?.status) {
      setAttendance(res.data.attendance);
      setIsClockedIn(res.data.is_clocked_in);
      setCutoff(res.data.clock_in_cutoff);
      setRequiresClocking(res.data.requires_clocking);
      setPendingLeave(res.data.pending_leave);
      setActiveLeave(res.data.active_leave);
    }
    setLoading(false);
  };

  const handleClick = async (type) => {
    if (type === "leave" || type === "lop") {
      setLeaveFrom(moment().format("YYYY-MM-DD"));
      setLeaveTo(moment().format("YYYY-MM-DD"));
      setLeaveReason("");
      setLeaveType(type);
      setShowLeave(true);
      return;
    }
    setSubmitting(true);
    const res = await markAttendance(type);
    if (res) {
      (res.status ? toast.success : toast.info)(res.message);
      await loadStatus();
    }
    setSubmitting(false);
  };

  const submitLeave = async () => {
    if (!leaveReason.trim()) { toast.error("Please enter reason for leave"); return; }
    setSubmitting(true);
    const res = await applyLeave({
      from_date: leaveFrom,
      to_date: leaveTo,
      reason: leaveReason,
      leave_type: leaveType,
      is_lop: leaveType === "lop"
    });
    setSubmitting(false);
    if (res) {
      (res.status ? toast.success : toast.info)(res.message);
      if (res.status) {
        setShowLeave(false);
        await loadStatus();
      }
    }
  };

  const handleClockOut = async () => {
    setSubmitting(true);
    const res = await clockOut();
    if (res) {
      (res.status ? toast.success : toast.info)(res.message);
      await loadStatus();
    }
    setSubmitting(false);
  };

  const cutoffLabel = cutoff ? moment(cutoff, "HH:mm:ss").format("hh:mm A") : "";
  const currentType = attendance?.attendance_type;
  const isClockedOut = attendance?.status === "clocked_out";

  const wrap = (c) => embedded ? c : (
    <div className="app-content main-content">
      <div className="side-app main-container">
        <div className="page-header d-xl-flex d-block">
          <div className="page-leftheader"><div className="page-title">Attendance</div></div>
        </div>
        {c}
      </div>
    </div>
  );

  if (loading) return embedded ? null : wrap(
    <div className="row"><div className="col-xl-12"><div className="card"><div className="card-body text-center"><div className="spinner-border" role="status"><span className="sr-only">Loading...</span></div></div></div></div></div>
  );

  if (!requiresClocking) return embedded ? null : wrap(
    <div className="row"><div className="col-xl-12"><div className="card"><div className="card-body"><div className="alert alert-info mb-0 text-center">Attendance marking is not required for your role.</div></div></div></div></div>
  );

  return wrap(
    <div className="row">
      <div className="col-xl-12">
        <div className="card">
          <div className="card-header">
            <h2 className="main-content-title tx-24 mg-b-5">Mark Today's Attendance</h2>
            {cutoffLabel && <span className="ms-auto text-muted"><i className="fa fa-clock me-1"></i> Cutoff: {cutoffLabel}</span>}
          </div>
          <div className="card-body">
            {/* status badge */}
            {attendance && (
              <div className="alert alert-info d-flex align-items-center flex-wrap gap-2">
                <strong>Today: </strong>
                {(() => {
                  const info = ATTENDANCE_TYPES.find(t => t.value === attendance.attendance_type);
                  return info ? (
                    <span className="badge" style={{ backgroundColor: info.color, fontSize: "14px", padding: "6px 14px" }}>
                      <i className={`fa ${info.icon} me-1`}></i> {info.label}
                    </span>
                  ) : null;
                })()}
                {attendance.clock_in && <span className="ms-2">In: {moment(attendance.clock_in).format("hh:mm A")}</span>}
                {attendance.clock_out && <span className="ms-2">Out: {moment(attendance.clock_out).format("hh:mm A")}</span>}
                {attendance.working_hours && <span className="ms-2">Hrs: {attendance.working_hours}</span>}
              </div>
            )}

            {/* ── Show leave status instead of buttons when on leave ── */}
            {(pendingLeave || activeLeave || currentType === "leave" || currentType === "lop") && (
              <div className="alert alert-warning text-center mb-0" style={{ fontWeight: 600, fontSize: "16px", border: "3px solid #f9a825", borderRadius: "16px" }}>
                <i className="fa fa-calendar-alt me-2" style={{ color: "#f9a825" }}></i>
                {activeLeave
                  ? `You are on approved ${activeLeave.is_lop ? 'LOP' : 'leave'} today (${moment(activeLeave.from_date).format("DD/MM/YYYY")} - ${moment(activeLeave.to_date).format("DD/MM/YYYY")})`
                  : pendingLeave
                  ? `${pendingLeave.is_lop ? 'LOP' : 'Leave'} application pending from ${moment(pendingLeave.from_date).format("DD/MM/YYYY")} to ${moment(pendingLeave.to_date).format("DD/MM/YYYY")}`
                  : `You are on ${currentType === "lop" ? "LOP" : "leave"} today`}
              </div>
            )}

            {/* 5 buttons — only show when no leave and no attendance marked */}
            {(!currentType || currentType === "absent") && !pendingLeave && !activeLeave && currentType !== "leave" && currentType !== "lop" && (
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                {ATTENDANCE_TYPES.map(t => {
                  const disabled = submitting || (currentType && currentType !== "absent");
                  return (
                    <button key={t.value} className="btn btn-success me-2"
                      style={{ backgroundColor: t.color, borderColor: t.color}}
                      disabled={disabled}
                      onClick={() => handleClick(t.value)}
                    >
                      <i className={`fa ${t.icon} me-2`}></i> {t.label}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Clock Out */}
            {isClockedIn && (
              <div className="text-center mt-4">
                <button className="btn btn-danger btn-lg px-5" onClick={handleClockOut} disabled={submitting}
                  style={{ fontWeight: 600, fontSize: "18px" }}>
                  <i className="fa fa-sign-out-alt me-2"></i> Clock Out
                </button>
              </div>
            )}

            {/* already marked */}
            {currentType && currentType !== "absent" && currentType !== "leave" && currentType !== "lop" && !isClockedIn && (
              <div className="alert alert-success text-center mt-3 mb-0">
                <i className="fa fa-check-circle me-1"></i> Attendance marked for today
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Leave modal */}
      <Modal show={showLeave} onHide={() => setShowLeave(false)} size="md" centered>
        <Modal.Header closeButton style={{ borderBottom: "3px solid #f9a825" }}>
          <h2 className="main-content-title tx-24 mg-b-5">
            <i className="fa fa-calendar-alt me-2" style={{ color: "#f9a825" }}></i> Apply Leave / LOP
          </h2>
        </Modal.Header>
        <Modal.Body style={{ padding: "24px" }}>
          <div className="row">
            <div className="col-md-12 mb-3">
              <label className="form-label">Leave Type <span className="text-danger">*</span></label>
              <select className="form-control" value={leaveType} onChange={e => setLeaveType(e.target.value)}>
                <option value="leave">Leave (Normal Leave)</option>
                <option value="lop">LOP (Loss of Pay)</option>
              </select>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">From Date <span className="text-danger">*</span></label>
                <input type="date" className="form-control" value={leaveFrom}
                  onChange={e => setLeaveFrom(e.target.value)} min={moment().format("YYYY-MM-DD")} />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">To Date <span className="text-danger">*</span></label>
                <input type="date" className="form-control" value={leaveTo}
                  onChange={e => setLeaveTo(e.target.value)} min={leaveFrom || moment().format("YYYY-MM-DD")} />
              </div>
            </div>
            <div className="col-md-12">
              <div className="mb-3">
                <label className="form-label">Reason for {leaveType === "lop" ? "LOP" : "Leave"} <span className="text-danger">*</span></label>
                <textarea className="form-control" rows="3" placeholder={`Enter reason for ${leaveType === "lop" ? "LOP" : "leave"}`}
                  value={leaveReason} onChange={e => setLeaveReason(e.target.value)}></textarea>
              </div>
            </div>
          </div>
          <div className="text-end">
            <button className="btn btn-success me-2" onClick={submitLeave} disabled={submitting}
              style={{ fontWeight: 600 }}>
              <i className="fa fa-paper-plane me-1"></i> {submitting ? "Submitting..." : `Submit ${leaveType === "lop" ? "LOP" : "Leave"} Request`}
            </button>
            <button className="btn btn-secondary" onClick={() => setShowLeave(false)} style={{ fontWeight: 600 }}>
              Cancel
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}
